<template>
  <div class="mod-finance-profit-closing">
    <el-form :inline="true">
      <el-form-item label="会计年度">
        <el-select v-model="query.year" size="small" style="width:100px" filterable @change="onYearChange">
          <el-option v-for="y in yearOptions" :key="y" :label="y" :value="y" />
        </el-select>
      </el-form-item>
      <el-form-item label="期号">
        <el-select v-model="query.periodNo" size="small" style="width:80px" @change="onPeriodChange">
          <el-option v-for="n in periodNoOptions" :key="n" :label="n" :value="n" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button
          v-if="isAuth('finance:profitClosing:preview') || isAuth('finance:profitClosing:list')"
          type="primary"
          size="small"
          :loading="loading"
          @click="loadPreview"
        >预览</el-button>
      </el-form-item>
      <el-form-item>
        <el-button
          v-if="isAuth('finance:profitClosing:execute') && preview && !preview.periodClosed && !preview.closed && preview.lines && preview.lines.length"
          type="success"
          size="small"
          :loading="executing"
          @click="executeHandle"
        >执行结转</el-button>
      </el-form-item>
      <el-form-item>
        <el-button
          v-if="isAuth('finance:profitClosing:reverse') && preview && !preview.periodClosed && preview.closed"
          type="warning"
          size="small"
          :loading="reversing"
          @click="reverseHandle"
        >反结转</el-button>
      </el-form-item>
    </el-form>

    <el-alert
      v-if="preview && preview.closed"
      type="success"
      :closable="false"
      show-icon
      style="margin-bottom: 12px;"
      :title="(preview.periodClosed ? '该期间已结账，' : '') + '损益已结转，凭证号：' + (preview.voucherCode || '-') + '，凭证状态：' + (preview.voucherStatus === '2' ? '已记账' : '未记账') + '，本期利润：' + money(preview.profitAmount)"
    />
    <el-alert
      v-else-if="preview && preview.periodClosed && preview.lines && preview.lines.length"
      type="warning"
      :closable="false"
      show-icon
      style="margin-bottom: 12px;"
      title="该期间已结账但仍有待结转损益，请先反结账并核对凭证"
    />
    <el-alert
      v-else-if="preview && (!preview.lines || !preview.lines.length)"
      type="info"
      :closable="false"
      show-icon
      style="margin-bottom: 12px;"
      title="本期损益类科目无余额，无需结转"
    />

    <el-card v-if="preview && preview.profitSubjectName" shadow="never" style="margin-bottom: 12px;">
      <div slot="header">结转概要</div>
      <el-descriptions :column="3" size="small" border>
        <el-descriptions-item label="会计期间">{{ preview.year }} 年第 {{ preview.periodNo }} 期</el-descriptions-item>
        <el-descriptions-item label="本年利润科目">{{ preview.profitSubjectCode }} {{ preview.profitSubjectName }}</el-descriptions-item>
        <el-descriptions-item label="本期利润(贷-借)">{{ money(preview.profitAmount) }}</el-descriptions-item>
      </el-descriptions>
    </el-card>

    <el-table
      v-if="preview && preview.lines && preview.lines.length"
      :data="preview.lines"
      border
      v-loading="loading"
      style="margin-bottom: 12px;"
      :header-cell-style="headStyle"
    >
      <el-table-column type="index" label="序号" width="60" align="center" />
      <el-table-column prop="subjectCode" label="科目编码" width="120" />
      <el-table-column prop="subjectName" label="科目名称" min-width="160" />
      <el-table-column prop="closingDebit" label="期末借方" width="110" align="right">
        <template slot-scope="scope">{{ money(scope.row.closingDebit) }}</template>
      </el-table-column>
      <el-table-column prop="closingCredit" label="期末贷方" width="110" align="right">
        <template slot-scope="scope">{{ money(scope.row.closingCredit) }}</template>
      </el-table-column>
      <el-table-column prop="transferDebit" label="结转借方" width="110" align="right">
        <template slot-scope="scope">{{ money(scope.row.transferDebit) }}</template>
      </el-table-column>
      <el-table-column prop="transferCredit" label="结转贷方" width="110" align="right">
        <template slot-scope="scope">{{ money(scope.row.transferCredit) }}</template>
      </el-table-column>
    </el-table>

    <el-card v-if="preview && preview.voucherEntries && preview.voucherEntries.length" shadow="never">
      <div slot="header">凭证分录预览</div>
      <el-table :data="preview.voucherEntries" border size="small" :header-cell-style="headStyle">
        <el-table-column prop="lineSort" label="行号" width="60" align="center" />
        <el-table-column prop="summary" label="摘要" min-width="120" />
        <el-table-column prop="subjectCode" label="科目编码" width="120" />
        <el-table-column prop="subjectName" label="科目名称" min-width="160" />
        <el-table-column prop="debitAmount" label="借方金额" width="110" align="right">
          <template slot-scope="scope">{{ money(scope.row.debitAmount) }}</template>
        </el-table-column>
        <el-table-column prop="creditAmount" label="贷方金额" width="110" align="right">
          <template slot-scope="scope">{{ money(scope.row.creditAmount) }}</template>
        </el-table-column>
      </el-table>
    </el-card>
  </div>
</template>

<script>
import request from '../../api/request'
import auth from './financeAuth'

export default {
  mixins: [auth],
  data() {
    return {
      query: { year: '', periodNo: 1, financePeriodLineId: '' },
      periods: [],
      preview: null,
      executing: false,
      reversing: false
    }
  },
  computed: {
    yearOptions() {
      const years = []
      this.periods.forEach(p => {
        const y = String(p.year || '')
        if (y && years.indexOf(y) < 0) years.push(y)
      })
      return years.sort((a, b) => Number(b) - Number(a))
    },
    periodNoOptions() {
      if (!this.query.year) return []
      const nos = this.periods
        .filter(p => String(p.year) === String(this.query.year))
        .map(p => Number(p.periodNo))
        .filter(n => Number.isFinite(n))
      return [...new Set(nos)].sort((a, b) => a - b)
    }
  },
  created() {
    this.loadPeriods()
  },
  methods: {
    loadPeriods() {
      request.get('/finance/period/list').then(res => {
        this.periods = res.data || []
        if (this.periods.length && !this.query.year) {
          this.query.year = this.yearOptions[0] || ''
          const first = this.periods.find(p => String(p.year) === String(this.query.year))
          this.query.periodNo = first ? Number(first.periodNo) : 1
          this.syncPeriodLineId()
        }
      }).catch(() => {})
    },
    onYearChange() {
      const nos = this.periodNoOptions
      if (nos.length && nos.indexOf(Number(this.query.periodNo)) < 0) {
        this.query.periodNo = nos[0]
      }
      this.syncPeriodLineId()
      this.preview = null
    },
    onPeriodChange() {
      this.syncPeriodLineId()
      this.preview = null
    },
    syncPeriodLineId() {
      const line = this.periods.find(p => String(p.year) === String(this.query.year) && Number(p.periodNo) === Number(this.query.periodNo))
      this.query.financePeriodLineId = line ? line.financePeriodLineId : ''
    },
    loadPreview() {
      if (!this.query.year || !this.query.periodNo) {
        this.$message.warning('请选择会计期间')
        return
      }
      this.syncPeriodLineId()
      if (!this.query.financePeriodLineId) {
        this.$message.warning('会计期间不存在')
        return
      }
      this.loading = true
      request.post('/finance/profitClosing/preview', {
        year: this.query.year,
        periodNo: this.query.periodNo,
        financePeriodLineId: this.query.financePeriodLineId,
        id: this.query.financePeriodLineId
      }).then(res => {
        this.preview = res.data || null
      }).catch(() => { this.preview = null }).finally(() => { this.loading = false })
    },
    executeHandle() {
      this.$confirm('确认执行损益结转？系统将生成「转」字凭证。', '提示', { type: 'warning' }).then(() => {
        this.executing = true
        request.post('/finance/profitClosing/execute', {
          year: this.query.year,
          periodNo: this.query.periodNo,
          financePeriodLineId: this.query.financePeriodLineId,
          id: this.query.financePeriodLineId
        }).then(() => {
          this.$message.success('损益结转成功')
          this.loadPreview()
        }).catch(() => {}).finally(() => { this.executing = false })
      }).catch(() => {})
    },
    reverseHandle() {
      this.$confirm('确认反结转？将删除结转凭证并恢复损益科目余额。', '提示', { type: 'warning' }).then(() => {
        this.reversing = true
        request.post('/finance/profitClosing/reverse', {
          year: this.query.year,
          periodNo: this.query.periodNo,
          financePeriodLineId: this.query.financePeriodLineId,
          id: this.query.financePeriodLineId
        }).then(() => {
          this.$message.success('反结转成功')
          this.loadPreview()
        }).catch(() => {}).finally(() => { this.reversing = false })
      }).catch(() => {})
    }
  }
}
</script>
